import { LoadingState } from '../ui';
// src/pages/Squad.jsx
import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import SidebarNavbar from '../components/SidebarNavbar';
import BottomNavbar from '../components/BottomNavbar';
import AddFriendModal from '../components/AddFriendModal';
import CreateSquadModal from '../components/CreateSquadModal';
import { useNavigate } from 'react-router-dom';
import notify from '../lib/notify';
import { MAX_FRIENDS, FRIENDS_PAGE_SIZE } from '../lib/config';
import { Button as FcButton, Card as FcCard, Input as FcInput } from '../ui';
import { Tabs as FcTabs } from '../ui';

export default function Squad({ session, userRole }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('friends'); // friends, mySquads, discover, requests
  
  // Modal states
  const [showAddFriendModal, setShowAddFriendModal] = useState(false);
  const [showCreateSquadModal, setShowCreateSquadModal] = useState(false);
  
  // Friends data (server-paginated + searchable via get_friends_page)
  const [friends, setFriends] = useState([]);
  const [friendCount, setFriendCount] = useState(0);
  const [friendSearch, setFriendSearch] = useState('');
  const [friendsHasMore, setFriendsHasMore] = useState(false);
  const [loadingMoreFriends, setLoadingMoreFriends] = useState(false);
  
  // Squads data
  const [mySquads, setMySquads] = useState([]);
  const [squadCount, setSquadCount] = useState(0);
  const [discoverSquads, setDiscoverSquads] = useState([]);
  
  // Requests data (incoming)
  const [friendRequests, setFriendRequests] = useState([]);
  const [squadJoinRequests, setSquadJoinRequests] = useState([]);
  
  // Outgoing requests (sent by me)
  const [outgoingFriendRequests, setOutgoingFriendRequests] = useState([]);
  const [outgoingSquadRequests, setOutgoingSquadRequests] = useState([]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  useEffect(() => {
    if (session?.user?.id) {
      fetchData();
    }
  }, [session?.user?.id, activeTab]);

  // Debounced friend search -> refetch the first page (skip initial mount).
  const friendSearchMounted = React.useRef(false);
  useEffect(() => {
    if (!friendSearchMounted.current) { friendSearchMounted.current = true; return; }
    if (activeTab !== 'friends' || !session?.user?.id) return;
    const t = setTimeout(() => { fetchFriends(friendSearch); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [friendSearch]);

  const fetchData = async () => {
    setLoading(true);
    
    if (activeTab === 'friends') {
      await fetchFriends();
    } else if (activeTab === 'mySquads') {
      await fetchMySquads();
    } else if (activeTab === 'discover') {
      await fetchDiscoverSquads();
    } else if (activeTab === 'requests') {
      await fetchRequests();
    }
    
    setLoading(false);
  };

  const fetchFriends = async (search = friendSearch) => {
    try {
      // Total friend count (unfiltered) for the header.
      const { data: countData, error: countError } = await supabase
        .rpc('get_friend_count', { p_user_id: session.user.id });
      if (countError) throw countError;
      setFriendCount(countData || 0);

      // First page of friends (server-side search + pagination).
      const { data, error } = await supabase.rpc('get_friends_page', {
        p_search: search || null,
        p_limit: FRIENDS_PAGE_SIZE,
        p_offset: 0,
      });
      if (error) throw error;
      const page = data || [];
      setFriends(page);
      setFriendsHasMore(page.length === FRIENDS_PAGE_SIZE);
    } catch (error) {
      notify('Failed to load friends', 'error');
    }
  };

  const loadMoreFriends = async () => {
    if (loadingMoreFriends) return;
    setLoadingMoreFriends(true);
    try {
      const { data, error } = await supabase.rpc('get_friends_page', {
        p_search: friendSearch || null,
        p_limit: FRIENDS_PAGE_SIZE,
        p_offset: friends.length,
      });
      if (error) throw error;
      const page = data || [];
      setFriends((prev) => [...prev, ...page]);
      setFriendsHasMore(page.length === FRIENDS_PAGE_SIZE);
    } catch (error) {
      notify('Failed to load more friends', 'error');
    } finally {
      setLoadingMoreFriends(false);
    }
  };

  const fetchMySquads = async () => {
    try {
      // Get squad count
      const { data: countData, error: countError } = await supabase
        .rpc('get_squad_count', { p_user_id: session.user.id });
      
      if (countError) {
        throw countError;
      }
      setSquadCount(countData || 0);

      // Get user's squads
      const { data: membershipsData, error: membershipsError } = await supabase
        .from('squad_members')
        .select('squad_id, role, squads(id, name, description, created_by)')
        .eq('user_id', session.user.id);

      if (membershipsError) {
        throw membershipsError;
      }

      // Get member counts for each squad
      const squadsWithCounts = await Promise.all(
        (membershipsData || []).map(async (membership) => {
          const { count } = await supabase
            .from('squad_members')
            .select('*', { count: 'exact', head: true })
            .eq('squad_id', membership.squad_id);

          return {
            ...membership.squads,
            role: membership.role,
            memberCount: count || 0
          };
        })
      );

      setMySquads(squadsWithCounts);
    } catch (error) {
      notify('Failed to load squads', 'error');
    }
  };

  const fetchDiscoverSquads = async () => {
    try {
      // Get all squads
      const { data: squadsData, error: squadsError } = await supabase
        .from('squads')
        .select('id, name, created_by')
        .order('created_at', { ascending: false });

      if (squadsError) throw squadsError;

      // Get creator usernames separately
      const creatorIds = [...new Set(squadsData?.map(s => s.created_by) || [])];
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, username')
        .in('id', creatorIds);

      if (profilesError) throw profilesError;

      // Create a map of creator IDs to usernames
      const creatorMap = {};
      profilesData?.forEach(profile => {
        creatorMap[profile.id] = profile.username;
      });

      // Get member counts and check if user is member or has pending request
      const squadsWithInfo = await Promise.all(
        (squadsData || []).map(async (squad) => {
          const [memberCountResult, isMemberResult, hasPendingResult] = await Promise.all([
            supabase
              .from('squad_members')
              .select('*', { count: 'exact', head: true })
              .eq('squad_id', squad.id),
            supabase
              .from('squad_members')
              .select('id')
              .eq('squad_id', squad.id)
              .eq('user_id', session.user.id)
              .single(),
            supabase
              .from('squad_join_requests')
              .select('id')
              .eq('squad_id', squad.id)
              .eq('requester_id', session.user.id)
              .eq('status', 'pending')
              .single()
          ]);

          return {
            ...squad,
            creatorUsername: creatorMap[squad.created_by] || 'Unknown',
            memberCount: memberCountResult.count || 0,
            isMember: !isMemberResult.error,
            hasPendingRequest: !hasPendingResult.error
          };
        })
      );

      setDiscoverSquads(squadsWithInfo);
    } catch (error) {
      notify('Failed to load squads', 'error');
    }
  };

  const fetchRequests = async () => {
    try {
      // Fetch friend requests
      const { data: friendReqData, error: friendReqError } = await supabase
        .from('friend_requests')
        .select('id, sender_id, status, created_at')
        .eq('receiver_id', session.user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (friendReqError) throw friendReqError;

      // Fetch sender profiles separately
      if (friendReqData && friendReqData.length > 0) {
        const senderIds = friendReqData.map(req => req.sender_id);
        const { data: sendersData, error: sendersError } = await supabase
          .from('profiles')
          .select('id, username')
          .in('id', senderIds);

        if (sendersError) throw sendersError;

        // Map usernames to requests
        const senderMap = {};
        sendersData?.forEach(sender => {
          senderMap[sender.id] = sender.username;
        });

        const friendRequestsWithSenders = friendReqData.map(req => ({
          ...req,
          sender: { username: senderMap[req.sender_id] || 'Unknown' }
        }));

        setFriendRequests(friendRequestsWithSenders);
      } else {
        setFriendRequests([]);
      }

      // Fetch squad join requests for squads user manages
      const { data: adminSquads, error: squadsError } = await supabase
        .from('squad_members')
        .select('squad_id')
        .eq('user_id', session.user.id)
        .in('role', ['creator', 'admin']);

      if (squadsError) throw squadsError;

      const squadIds = adminSquads?.map(s => s.squad_id) || [];

      if (squadIds.length > 0) {
        const { data: joinReqData, error: joinReqError } = await supabase
          .from('squad_join_requests')
          .select('id, squad_id, requester_id, status, created_at')
          .in('squad_id', squadIds)
          .eq('status', 'pending')
          .order('created_at', { ascending: false });

        if (joinReqError) throw joinReqError;

        if (joinReqData && joinReqData.length > 0) {
          // Fetch squad names
          const { data: squadsData, error: squadsDataError } = await supabase
            .from('squads')
            .select('id, name')
            .in('id', squadIds);

          if (squadsDataError) throw squadsDataError;

          // Fetch requester profiles
          const requesterIds = joinReqData.map(req => req.requester_id);
          const { data: requestersData, error: requestersError } = await supabase
            .from('profiles')
            .select('id, username')
            .in('id', requesterIds);

          if (requestersError) throw requestersError;

          // Create maps
          const squadMap = {};
          squadsData?.forEach(squad => {
            squadMap[squad.id] = squad.name;
          });

          const requesterMap = {};
          requestersData?.forEach(requester => {
            requesterMap[requester.id] = requester.username;
          });

          // Combine data
          const joinRequestsWithInfo = joinReqData.map(req => ({
            ...req,
            squads: { name: squadMap[req.squad_id] || 'Unknown' },
            requester: { username: requesterMap[req.requester_id] || 'Unknown' }
          }));

          setSquadJoinRequests(joinRequestsWithInfo);
        } else {
          setSquadJoinRequests([]);
        }
      } else {
        setSquadJoinRequests([]);
      }

      // Fetch OUTGOING friend requests (sent by me)
      const { data: outgoingFriendReqData, error: outgoingFriendError } = await supabase
        .from('friend_requests')
        .select('id, receiver_id, status, created_at')
        .eq('sender_id', session.user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (outgoingFriendError) throw outgoingFriendError;

      // Fetch receiver profiles
      if (outgoingFriendReqData && outgoingFriendReqData.length > 0) {
        const receiverIds = outgoingFriendReqData.map(req => req.receiver_id);
        const { data: receiversData, error: receiversError } = await supabase
          .from('profiles')
          .select('id, username')
          .in('id', receiverIds);

        if (receiversError) throw receiversError;

        const receiverMap = {};
        receiversData?.forEach(receiver => {
          receiverMap[receiver.id] = receiver.username;
        });

        const outgoingFriendRequestsWithReceivers = outgoingFriendReqData.map(req => ({
          ...req,
          receiver: { username: receiverMap[req.receiver_id] || 'Unknown' }
        }));

        setOutgoingFriendRequests(outgoingFriendRequestsWithReceivers);
      } else {
        setOutgoingFriendRequests([]);
      }

      // Fetch OUTGOING squad join requests (sent by me)
      const { data: outgoingSquadReqData, error: outgoingSquadError } = await supabase
        .from('squad_join_requests')
        .select('id, squad_id, status, created_at')
        .eq('requester_id', session.user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (outgoingSquadError) throw outgoingSquadError;

      if (outgoingSquadReqData && outgoingSquadReqData.length > 0) {
        // Fetch squad names
        const outgoingSquadIds = outgoingSquadReqData.map(req => req.squad_id);
        const { data: outgoingSquadsData, error: outgoingSquadsError } = await supabase
          .from('squads')
          .select('id, name')
          .in('id', outgoingSquadIds);

        if (outgoingSquadsError) throw outgoingSquadsError;

        const outgoingSquadMap = {};
        outgoingSquadsData?.forEach(squad => {
          outgoingSquadMap[squad.id] = squad.name;
        });

        const outgoingSquadRequestsWithInfo = outgoingSquadReqData.map(req => ({
          ...req,
          squads: { name: outgoingSquadMap[req.squad_id] || 'Unknown' }
        }));

        setOutgoingSquadRequests(outgoingSquadRequestsWithInfo);
      } else {
        setOutgoingSquadRequests([]);
      }
    } catch (error) {
      notify('Failed to load requests', 'error');
    }
  };

  const handleAcceptFriendRequest = async (requestId, senderId) => {
    try {
      // Create friendship (ensure user1_id < user2_id)
      const user1 = senderId < session.user.id ? senderId : session.user.id;
      const user2 = senderId < session.user.id ? session.user.id : senderId;

      const { error: friendshipError } = await supabase
        .from('friendships')
        .insert({ user1_id: user1, user2_id: user2 });

      if (friendshipError) throw friendshipError;

      // Update request status
      const { error: updateError } = await supabase
        .from('friend_requests')
        .update({ status: 'accepted', responded_at: new Date().toISOString() })
        .eq('id', requestId);

      if (updateError) throw updateError;

      notify('Friend request accepted!', 'success');
      fetchRequests();
    } catch (error) {
      if (error.message.includes('200 friend limit')) {
        notify(error.message, 'error');
      } else {
        notify('Failed to accept friend request', 'error');
      }
    }
  };

  const handleRejectFriendRequest = async (requestId) => {
    try {
      const { error } = await supabase
        .from('friend_requests')
        .update({ status: 'rejected', responded_at: new Date().toISOString() })
        .eq('id', requestId);

      if (error) throw error;

      notify('Friend request rejected', 'success');
      fetchRequests();
    } catch (error) {
      notify('Failed to reject friend request', 'error');
    }
  };

  const handleApproveSquadJoinRequest = async (requestId, squadId, requesterId) => {
    try {
      // Add user to squad_members
      const { error: memberError } = await supabase
        .from('squad_members')
        .insert({
          squad_id: squadId,
          user_id: requesterId,
          role: 'member'
        });

      if (memberError) throw memberError;

      // Update request status
      const { error: updateError } = await supabase
        .from('squad_join_requests')
        .update({ status: 'accepted', responded_at: new Date().toISOString() })
        .eq('id', requestId);

      if (updateError) throw updateError;

      notify('Squad join request approved!', 'success');
      fetchRequests();
    } catch (error) {
      notify('Failed to approve request', 'error');
    }
  };

  const handleRejectSquadJoinRequest = async (requestId) => {
    try {
      const { error } = await supabase
        .from('squad_join_requests')
        .update({ status: 'rejected', responded_at: new Date().toISOString() })
        .eq('id', requestId);

      if (error) throw error;

      notify('Squad join request rejected', 'success');
      fetchRequests();
    } catch (error) {
      notify('Failed to reject request', 'error');
    }
  };

  const handleCancelFriendRequest = async (requestId) => {
    try {
      const { error } = await supabase
        .from('friend_requests')
        .delete()
        .eq('id', requestId);

      if (error) throw error;

      notify('Friend request cancelled', 'success');
      fetchRequests();
    } catch (error) {
      notify('Failed to cancel request', 'error');
    }
  };

  const handleCancelSquadRequest = async (requestId) => {
    try {
      const { error } = await supabase
        .from('squad_join_requests')
        .delete()
        .eq('id', requestId);

      if (error) throw error;

      notify('Squad join request cancelled', 'success');
      fetchRequests();
      fetchDiscoverSquads(); // Refresh discover to update button state
    } catch (error) {
      notify('Failed to cancel request', 'error');
    }
  };

  const handleRequestToJoinSquad = async (squadId) => {
    try {
      const { error } = await supabase
        .from('squad_join_requests')
        .insert({
          squad_id: squadId,
          requester_id: session.user.id
        });

      if (error) {
        if (error.code === '23505') { // Unique violation
          notify('You already have a pending request for this squad', 'info');
        } else {
          throw error;
        }
      } else {
        notify('Join request sent!', 'success');
        fetchDiscoverSquads();
      }
    } catch (error) {
      notify('Failed to send join request', 'error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-canvas">
        <div className="block md:hidden w-full bg-ui-green py-3 px-4">
          <span className="text-white text-lg font-semibold">Footy Coach</span>
        </div>
        <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
        <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
        <div role="main" id="fc-main-content" className="md:ml-64 pt-8 px-2 sm:px-4 md:p-8 fc-workspace">
          <LoadingState label="Loading..." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-canvas">
      <div className="block md:hidden w-full bg-ui-green py-3 px-4">
        <span className="text-white text-lg font-semibold">Footy Coach</span>
      </div>
      <SidebarNavbar handleLogout={handleLogout} userRole={userRole} />
      <BottomNavbar handleLogout={handleLogout} userRole={userRole} />
      
      <div role="main" id="fc-main-content" className="md:ml-64 pt-8 px-2 sm:px-4 md:p-8 pb-28 md:pb-8 fc-workspace">
        <h1 className="mb-4">Squad</h1>

        {/* Tab Navigation */}
        <FcTabs label="Squad sections" value={activeTab} onValueChange={setActiveTab} items={[{id:"friends",label:<>
            Friends
          </>},{id:"mySquads",label:<>
            My Squads
          </>},{id:"discover",label:<>
            Discover Squads
          </>},{id:"requests",label:<>
            Requests
          </>}]}>

        {/* Tab Content */}
        {activeTab === 'friends' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-brand-text">Friends <span className="text-content-secondary">({friendCount}/{MAX_FRIENDS})</span></h3>
              <FcButton variant="primary" type="submit"
                onClick={() => setShowAddFriendModal(true)}
                className=""
              >
                Add Friend
              </FcButton>
            </div>

            {/* Search (server-side) — needed now friends can number in the thousands */}
            {(friendCount > FRIENDS_PAGE_SIZE || friendSearch) && (
              <FcInput aria-label="Search friends by username…"
                type="text"
                value={friendSearch}
                onChange={(e) => setFriendSearch(e.target.value)}
                placeholder="Search friends by username…"
                className="w-full border mb-4"
              />
            )}

            {friends.length === 0 ? (
              <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg p-6 text-center text-content-secondary">
                {friendSearch ? (
                  <p>No friends match “{friendSearch}”.</p>
                ) : (
                  <>
                    <p>You haven't added any friends yet.</p>
                    <p className="text-sm mt-2">Add friends to create squads and play together!</p>
                  </>
                )}
              </FcCard>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {friends.map((friend) => (
                    <FcCard as="div" padding="standard" key={friend.id} className="bg-surface-card rounded-lg p-4 shadow">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-brand-text">@{friend.username}</span>
                        <FcButton variant="tertiary" type="submit"
                          onClick={() => navigate(`/friend/${friend.id}`)}
                          className="hover:underline"
                        >
                          View Profile
                        </FcButton>
                      </div>
                    </FcCard>
                  ))}
                </div>
                {friendsHasMore && (
                  <div className="mt-4 text-center">
                    <FcButton variant="secondary" type="submit"
                      onClick={loadMoreFriends}
                      disabled={loadingMoreFriends}
                      className="border"
                    >
                      {loadingMoreFriends ? 'Loading…' : 'Load more'}
                    </FcButton>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'mySquads' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-brand-text">My Squads <span className="text-content-secondary">({squadCount}/50)</span></h3>
              <FcButton variant="primary" type="submit"
                onClick={() => setShowCreateSquadModal(true)}
                className=""
              >
                Create Squad
              </FcButton>
            </div>

            {mySquads.length === 0 ? (
              <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg p-6 text-center text-content-secondary">
                <p>You're not in any squads yet.</p>
                <p className="text-sm mt-2">Create or join a squad to organize competitions with friends!</p>
              </FcCard>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {mySquads.map((squad) => (
                  <FcCard as="div" padding="standard" key={squad.id} className="bg-surface-card rounded-lg p-4 shadow">
                    <h4 className="font-bold text-brand-text mb-2">{squad.name}</h4>
                    <p className="text-sm text-content-secondary mb-2">{squad.memberCount} members</p>
                    <span className="text-xs bg-gray-200 px-2 py-1 rounded">{squad.role}</span>
                    <FcButton variant="primary" type="submit"
                      onClick={() => navigate(`/squad/${squad.id}`)}
                      className="mt-3 w-full"
                    >
                      View Squad
                    </FcButton>
                  </FcCard>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'discover' && (
          <div>
            <h3 className="text-xl font-bold text-brand-text mb-4">Discover Squads</h3>

            {discoverSquads.length === 0 ? (
              <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg p-6 text-center text-content-secondary">
                <p>No squads available to discover.</p>
              </FcCard>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {discoverSquads.map((squad) => (
                  <FcCard as="div" padding="standard" key={squad.id} className="bg-surface-card rounded-lg p-4 shadow">
                    <h4 className="font-bold text-brand-text mb-2">{squad.name}</h4>
                    <p className="text-sm text-content-secondary mb-1">Created by: @{squad.creatorUsername}</p>
                    <p className="text-sm text-content-secondary mb-3">{squad.memberCount} members</p>
                    
                    {squad.isMember ? (
                      <FcButton variant="tertiary" type="submit"
                        onClick={() => navigate(`/squad/${squad.id}`)}
                        className="w-full"
                      >
                        Already a Member
                      </FcButton>
                    ) : squad.hasPendingRequest ? (
                      <FcButton variant="tertiary" type="submit"
                        disabled
                        className="w-full cursor-not-allowed"
                      >
                        Request Pending
                      </FcButton>
                    ) : (
                      <FcButton variant="primary" type="submit"
                        onClick={() => handleRequestToJoinSquad(squad.id)}
                        className="w-full"
                      >
                        Request to Join
                      </FcButton>
                    )}
                  </FcCard>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'requests' && (
          <div>
            <h3 className="text-xl font-bold text-brand-text mb-4">Requests <span className="text-content-secondary">({friendRequests.length + squadJoinRequests.length + outgoingFriendRequests.length + outgoingSquadRequests.length})</span></h3>

            {/* INCOMING REQUESTS */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-brand-text mb-3 flex items-center gap-2">
                <span className="text-2xl">📥</span>
                Incoming Requests
              </h4>
              
              {/* Friend Requests Received */}
              <div className="mb-6">
                <h5 className="text-md font-semibold text-content-primary mb-2">Friend Requests</h5>
                {friendRequests.length === 0 ? (
                  <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg p-4 text-center text-content-secondary">
                    <p className="text-sm">No pending friend requests</p>
                  </FcCard>
                ) : (
                  <div className="space-y-3">
                    {friendRequests.map((request) => (
                      <FcCard as="div" padding="standard" key={request.id} className="bg-surface-card rounded-lg p-4 shadow flex justify-between items-center">
                        <div>
                          <p className="font-semibold text-brand-text">@{request.sender?.username || 'Unknown'}</p>
                          <p className="text-sm text-content-secondary">
                            Sent {new Date(request.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <FcButton variant="primary" type="submit"
                            onClick={() => handleAcceptFriendRequest(request.id, request.sender_id)}
                            className=""
                          >
                            Accept
                          </FcButton>
                          <FcButton variant="tertiary" type="submit"
                            onClick={() => handleRejectFriendRequest(request.id)}
                            className=""
                          >
                            Reject
                          </FcButton>
                        </div>
                      </FcCard>
                    ))}
                  </div>
                )}
              </div>

              {/* Squad Join Requests Received */}
              <div>
                <h5 className="text-md font-semibold text-content-primary mb-2">Squad Join Requests</h5>
                {squadJoinRequests.length === 0 ? (
                  <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg p-4 text-center text-content-secondary">
                    <p className="text-sm">No pending squad join requests</p>
                  </FcCard>
                ) : (
                  <div className="space-y-3">
                    {squadJoinRequests.map((request) => (
                      <FcCard as="div" padding="standard" key={request.id} className="bg-surface-card rounded-lg p-4 shadow flex justify-between items-center">
                        <div>
                          <p className="font-semibold text-brand-text">@{request.requester?.username || 'Unknown'}</p>
                          <p className="text-sm text-content-secondary">wants to join <span className="font-semibold">{request.squads?.name}</span></p>
                          <p className="text-sm text-content-secondary">
                            Sent {new Date(request.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <FcButton variant="primary" type="submit"
                            onClick={() => handleApproveSquadJoinRequest(request.id, request.squad_id, request.requester_id)}
                            className=""
                          >
                            Approve
                          </FcButton>
                          <FcButton variant="tertiary" type="submit"
                            onClick={() => handleRejectSquadJoinRequest(request.id)}
                            className=""
                          >
                            Reject
                          </FcButton>
                        </div>
                      </FcCard>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* OUTGOING REQUESTS */}
            <div>
              <h4 className="text-lg font-semibold text-brand-text mb-3 flex items-center gap-2">
                <span className="text-2xl">📤</span>
                Sent Requests
              </h4>
              
              {/* Outgoing Friend Requests */}
              <div className="mb-6">
                <h5 className="text-md font-semibold text-content-primary mb-2">Friend Requests Sent</h5>
                {outgoingFriendRequests.length === 0 ? (
                  <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg p-4 text-center text-content-secondary">
                    <p className="text-sm">No pending outgoing friend requests</p>
                  </FcCard>
                ) : (
                  <div className="space-y-3">
                    {outgoingFriendRequests.map((request) => (
                      <FcCard as="div" padding="standard" key={request.id} className="bg-surface-card rounded-lg p-4 shadow flex justify-between items-center border-l-4 border-yellow-400">
                        <div>
                          <p className="font-semibold text-brand-text">@{request.receiver?.username || 'Unknown'}</p>
                          <p className="text-sm text-content-secondary">
                            <span className="text-yellow-600 font-semibold">Pending</span> · Sent {new Date(request.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div>
                          <FcButton variant="tertiary" type="submit"
                            onClick={() => handleCancelFriendRequest(request.id)}
                            className=""
                          >
                            Cancel
                          </FcButton>
                        </div>
                      </FcCard>
                    ))}
                  </div>
                )}
              </div>

              {/* Outgoing Squad Requests */}
              <div>
                <h5 className="text-md font-semibold text-content-primary mb-2">Squad Join Requests Sent</h5>
                {outgoingSquadRequests.length === 0 ? (
                  <FcCard as="div" padding="standard" className="bg-surface-card rounded-lg p-4 text-center text-content-secondary">
                    <p className="text-sm">No pending outgoing squad requests</p>
                  </FcCard>
                ) : (
                  <div className="space-y-3">
                    {outgoingSquadRequests.map((request) => (
                      <FcCard as="div" padding="standard" key={request.id} className="bg-surface-card rounded-lg p-4 shadow flex justify-between items-center border-l-4 border-yellow-400">
                        <div>
                          <p className="font-semibold text-brand-text">{request.squads?.name || 'Unknown Squad'}</p>
                          <p className="text-sm text-content-secondary">
                            <span className="text-yellow-600 font-semibold">Pending</span> · Sent {new Date(request.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div>
                          <FcButton variant="tertiary" type="submit"
                            onClick={() => handleCancelSquadRequest(request.id)}
                            className=""
                          >
                            Cancel
                          </FcButton>
                        </div>
                      </FcCard>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
</FcTabs>
      </div>
      
      {/* Modals */}
      <AddFriendModal 
        isOpen={showAddFriendModal}
        onClose={() => setShowAddFriendModal(false)}
        currentUserId={session?.user?.id}
        onSuccess={() => {
          fetchFriends();
          setActiveTab('friends');
        }}
      />
      
      <CreateSquadModal 
        isOpen={showCreateSquadModal}
        onClose={() => setShowCreateSquadModal(false)}
        currentUserId={session?.user?.id}
        onSuccess={() => {
          fetchMySquads();
          setActiveTab('mySquads');
        }}
      />
    </div>
  );
}
